import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn, hash } from "@ember/helper";
import { action } from "@ember/object";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import Form from "discourse/components/form";
import { ajax } from "discourse/lib/ajax";
import { extractError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { escapeExpression } from "discourse/lib/utilities";
import Category from "discourse/models/category";
import CategoryChooser from "discourse/select-kit/components/category-chooser";
import { not } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import I18n, { i18n } from "discourse-i18n";

const DEFAULT_HINT = trustHTML(
  i18n("chat.create_channel.choose_category.default_hint", {
    link: "/categories",
    category: "category",
  })
);

export default class ChatModalCreateChannel extends Component {
  @service dialog;
  @service chatChannelsManager;
  @service chatApi;
  @service router;
  @service siteSettings;

  @tracked flash;
  @tracked category;
  @tracked autoGeneratedSlug = "";
  @tracked categoryPermissionsHint = DEFAULT_HINT;
  @tracked autoJoinWarning = "";
  @tracked loadingPermissionHint = false;

  formApi;
  formData = {
    name: "",
    slug: "",
    description: "",
    chatable_id: null,
    emoji: null,
    auto_join_users: false,
    threading_enabled: false,
  };
  #generateSlugHandler = null;

  willDestroy() {
    super.willDestroy(...arguments);
    cancel(this.#generateSlugHandler);
  }

  get autoJoinAvailable() {
    return this.siteSettings.max_chat_auto_joined_users > 0;
  }

  get categoryName() {
    return this.category ? escapeExpression(this.category.name) : null;
  }

  @action
  registerApi(api) {
    this.formApi = api;
  }

  @action
  submit() {
    this.formApi.submit();
  }

  @action
  onCategoryChange(categoryId, { set }) {
    const category = categoryId ? Category.findById(categoryId) : null;
    set("chatable_id", categoryId);
    this.category = category;
    this.#updatePermissionsHint(category);

    const name = this.formApi.get("name") || category?.name || "";
    set("name", name);
    this.#debouncedGenerateSlug(name);
  }

  @action
  onNameChange(name, { set }) {
    set("name", name);
    this.#debouncedGenerateSlug(name);
  }

  @action
  onSubmit(data) {
    const payload = {
      name: data.name,
      slug: data.slug || this.autoGeneratedSlug,
      description: data.description,
      chatable_id: data.chatable_id,
      auto_join_users: data.auto_join_users,
      threading_enabled: data.threading_enabled,
      emoji: data.emoji,
    };

    if (payload.auto_join_users) {
      this.dialog.yesNoConfirm({
        message: this.autoJoinWarning,
        didConfirm: () => this.#createChannel(payload),
      });
    } else {
      this.#createChannel(payload);
    }
  }

  async #createChannel(data) {
    try {
      const channel = await this.chatApi.createChannel(data);

      this.args.closeModal();
      this.chatChannelsManager.follow(channel);
      this.router.transitionTo("chat.channel", ...channel.routeModels);
    } catch (e) {
      this.flash = extractError(e);
    }
  }

  #buildCategorySlug(category) {
    const parent = category.parentCategory;

    if (parent) {
      return `${this.#buildCategorySlug(parent)}/${category.slug}`;
    } else {
      return category.slug;
    }
  }

  #updateAutoJoinConfirmWarning(category, catPermissions) {
    const allowedGroups = catPermissions.allowed_groups;
    let warning;

    if (catPermissions.private) {
      switch (allowedGroups.length) {
        case 1:
          warning = i18n(
            "chat.create_channel.auto_join_users.warning_1_group",
            {
              count: catPermissions.members_count,
              group: escapeExpression(allowedGroups[0]),
            }
          );
          break;
        case 2:
          warning = i18n(
            "chat.create_channel.auto_join_users.warning_2_groups",
            {
              count: catPermissions.members_count,
              group1: escapeExpression(allowedGroups[0]),
              group2: escapeExpression(allowedGroups[1]),
            }
          );
          break;
        default:
          warning = I18n.messageFormat(
            "chat.create_channel.auto_join_users.warning_multiple_groups_MF",
            {
              groupCount: allowedGroups.length - 1,
              userCount: catPermissions.members_count,
              groupName: escapeExpression(allowedGroups[0]),
            }
          );
          break;
      }
    } else {
      warning = i18n(
        "chat.create_channel.auto_join_users.public_category_warning",
        {
          category: escapeExpression(category.name),
        }
      );
    }

    this.autoJoinWarning = warning;
  }

  #updatePermissionsHint(category) {
    if (category) {
      const fullSlug = this.#buildCategorySlug(category);

      this.loadingPermissionHint = true;

      return this.chatApi
        .categoryPermissions(category.id)
        .then((catPermissions) => {
          this.#updateAutoJoinConfirmWarning(category, catPermissions);
          const allowedGroups = catPermissions.allowed_groups;
          const settingLink = `/c/${escapeExpression(fullSlug)}/edit/security`;
          let hint;

          switch (allowedGroups.length) {
            case 1:
              hint = i18n("chat.create_channel.choose_category.hint_1_group", {
                settingLink,
                group: escapeExpression(allowedGroups[0]),
              });
              break;
            case 2:
              hint = i18n("chat.create_channel.choose_category.hint_2_groups", {
                settingLink,
                group1: escapeExpression(allowedGroups[0]),
                group2: escapeExpression(allowedGroups[1]),
              });
              break;
            default:
              hint = i18n(
                "chat.create_channel.choose_category.hint_multiple_groups",
                {
                  settingLink,
                  group: escapeExpression(allowedGroups[0]),
                  count: allowedGroups.length - 1,
                }
              );
              break;
          }

          this.categoryPermissionsHint = trustHTML(hint);
        })
        .finally(() => {
          this.loadingPermissionHint = false;
        });
    } else {
      this.categoryPermissionsHint = DEFAULT_HINT;
      this.autoJoinWarning = "";
    }
  }

  // intentionally not showing AJAX error for this, we will autogenerate
  // the slug server-side if they leave it blank
  #generateSlug(name) {
    return ajax("/slugs.json", { type: "POST", data: { name } }).then(
      (response) => {
        this.autoGeneratedSlug = response.slug;
      }
    );
  }

  #debouncedGenerateSlug(name) {
    cancel(this.#generateSlugHandler);
    this.autoGeneratedSlug = "";

    if (!name) {
      return;
    }

    this.#generateSlugHandler = discourseDebounce(
      this,
      this.#generateSlug,
      name,
      300
    );
  }

  <template>
    <DModal
      class="chat-modal-create-channel"
      @closeModal={{@closeModal}}
      @flash={{this.flash}}
      @inline={{@inline}}
      @title={{i18n "chat.create_channel.title"}}
    >
      <:body>
        <Form
          class="chat-modal-create-channel__form"
          @data={{this.formData}}
          @onRegisterApi={{this.registerApi}}
          @onSubmit={{this.onSubmit}}
          as |form|
        >
          <form.Field
            @format="full"
            @name="name"
            @onSet={{this.onNameChange}}
            @title={{i18n "chat.create_channel.name"}}
            @type="input"
            @validation="required"
            as |field|
          >
            <field.Control />
          </form.Field>

          <form.Field
            @format="full"
            @name="slug"
            @title={{i18n "chat.create_channel.slug"}}
            @tooltip={{i18n
              "chat.channel_edit_name_slug_modal.slug_description"
            }}
            @type="input"
            as |field|
          >
            <field.Control placeholder={{this.autoGeneratedSlug}} />
          </form.Field>

          <form.Field
            @format="full"
            @name="description"
            @title={{i18n "chat.create_channel.description"}}
            @type="input"
            as |field|
          >
            <field.Control />
          </form.Field>

          <form.Field
            @format="full"
            @name="chatable_id"
            @onSet={{this.onCategoryChange}}
            @title={{i18n "chat.create_channel.choose_category.label"}}
            @type="custom"
            @validation="required"
            as |field|
          >
            <field.Control>
              <CategoryChooser
                @onChange={{field.set}}
                @options={{hash
                  none="chat.create_channel.choose_category.none"
                }}
                @value={{field.value}}
              />

              {{#if this.categoryPermissionsHint}}
                <div
                  class={{dConcatClass
                    "chat-modal-create-channel__hint"
                    (if this.loadingPermissionHint "loading-permissions")
                  }}
                >
                  {{this.categoryPermissionsHint}}
                </div>
              {{/if}}
            </field.Control>
          </form.Field>

          {{#if this.siteSettings.enable_emoji}}
            <form.Field
              @name="emoji"
              @title={{i18n "chat.create_channel.emoji.label"}}
              @type="emoji"
              as |field|
            >
              <field.Control @context="channel-emoji" />

              <DButton
                class="btn-flat edit-channel-clear-emoji"
                @action={{fn field.set null}}
                @disabled={{not field.value}}
                @label="chat.create_channel.emoji.reset"
              />
            </form.Field>
          {{/if}}

          {{#if this.autoJoinAvailable}}
            <form.Field
              @format="full"
              @name="auto_join_users"
              @title={{i18n "chat.settings.auto_join_users_label"}}
              @type="checkbox"
              as |field|
            >
              <field.Control>
                {{#if this.categoryName}}
                  {{i18n
                    "chat.settings.auto_join_users_info"
                    category=this.categoryName
                  }}
                {{else}}
                  {{i18n "chat.settings.auto_join_users_info_no_category"}}
                {{/if}}
              </field.Control>
            </form.Field>
          {{/if}}

          <form.Field
            @format="full"
            @name="threading_enabled"
            @title={{i18n "chat.create_channel.threading.label"}}
            @type="checkbox"
            as |field|
          >
            <field.Control>
              {{i18n "chat.settings.channel_threading_description"}}
            </field.Control>
          </form.Field>
        </Form>
      </:body>
      <:footer>
        <DButton
          class="btn-primary create"
          @action={{this.submit}}
          @label="chat.create_channel.create"
        />
      </:footer>
    </DModal>
  </template>
}
